import { useState, useEffect, useCallback } from 'react';
import { checkUserGeolocation } from '../services/geolocationService';
import { wasGeolocationChecked, setGeolocationChecked } from '../utils';

interface UseGeolocationCheckResult {
  hasLocation: boolean;
  isChecking: boolean;
  error: string | null;
  recheckLocation: () => Promise<void>;
}

/**
 * Хук для проверки наличия геопозиции пользователя
 * Проверяет при каждом входе, но кэширует результат на сессию
 */
export function useGeolocationCheck(username: string): UseGeolocationCheckResult {
  const [hasLocation, setHasLocation] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkLocation = useCallback(async () => {
    // Если уже проверяли в этой сессии, пропускаем
    if (wasGeolocationChecked()) {
      console.log('Геопозиция уже проверена в этой сессии');
      setHasLocation(true);
      setIsChecking(false);
      return;
    }

    if (!username) {
      setError('Username не указан');
      setIsChecking(false);
      return;
    }

    try {
      setIsChecking(true);
      setError(null);

      const response = await checkUserGeolocation(username);

      if (response.success) {
        setHasLocation(response.hasLocation);
        
        // Если геоданные уже есть в БД, сохраняем флаг проверки
        // чтобы не проверять повторно в этой сессии
        if (response.hasLocation) {
          setGeolocationChecked();
          console.log('Геоданные найдены в БД, флаг проверки установлен');
        }
      } else {
        setError('Ошибка при проверке геопозиции');
      }
    } catch (err) {
      console.error('Ошибка при проверке геопозиции:', err);
      setError('Не удалось проверить геопозицию');
      // В случае ошибки проверки не блокируем пользователя
      setHasLocation(true);
    } finally {
      setIsChecking(false);
    }
  }, [username]);

  // Проверяем при монтировании компонента
  useEffect(() => {
    checkLocation();
  }, [checkLocation]);

  // Функция для повторной проверки
  const recheckLocation = useCallback(async () => {
    await checkLocation();
  }, [checkLocation]);

  return {
    hasLocation,
    isChecking,
    error,
    recheckLocation
  };
}

